/**
 * WS chat runtime — a pure reducer that turns the normalized WS chat event stream
 * (see protocol.ts) into renderable chat state. AG-UI-style: events in, UI
 * state out, no side effects. Fully unit-testable.
 */
import type { ChatMessage } from '../../../../types/agentChat';
import type { WsChatEvent } from './protocol';
import { canonicalWsToolName } from './toolNames';

/** Tool whose call pauses the turn to wait for the user's tap-to-answer. */
const ASK_QUESTION_TOOL_NAME = 'ASK_USER_QUESTION';

export type WsChatPhase = 'idle' | 'thinking' | 'streaming' | 'error';

export interface WsChatStreaming {
  id: string;
  text: string;
}

export type WsChatApprovalDecision = 'approve' | 'always' | 'deny';

/** A tool the agent invoked (or asked approval for) during the current turn. */
export interface WsChatToolEvent {
  id: string;
  name: string;
  kind: 'call' | 'approval';
  summary?: string;
  timeoutSec?: number;
  /** For calls: running until the matching tool_result arrives. */
  status?: 'running' | 'done';
  /** For calls: the tool's input arguments (from tool_call). Kept so the UI can
   *  render rich cards from a call's inputs (e.g. CREATE_CONTENT_DRAFT). */
  args?: unknown;
  /** For calls: the tool's output from tool_result — a string, or a structured
   *  object for ACP tools that return JSON (rich cards parse the object). */
  output?: unknown;
  /** For approvals: set once the user has responded. */
  decided?: WsChatApprovalDecision;
  /** Proxy-stamped stable frame id from tool_result; the idempotency key for report persistence. */
  eventId?: string | null;
}

/**
 * One node of a predefined task, as the server placed it.
 *
 * Kept apart from {@link WsChatToolEvent} deliberately: the tool trace answers
 * "what did it just call", while this answers "how far along is my request".
 * They are not the same list — the steps that take longest (writing the copy,
 * waiting on the merchant) call no tool at all and so never appear in the trace.
 */
export interface WsChatTaskStep {
  task: string;
  taskName: string;
  node: string;
  label: string;
  /** 1-based position in the task definition. */
  index: number;
  total: number;
  /**
   * `failed` means this step's work did not happen, so the label — which is a claim about
   * what exists now — does not hold. The step is still shown at its real position; it just
   * does not get a tick.
   */
  status: 'active' | 'done' | 'failed';
  /** Which failure it was, when the server said — see the protocol type. */
  failedReason?: 'tool_failed' | 'no_artifact';
  /** The run this step belongs to, when the frame said — see the protocol type and {@link splitTaskRuns}. */
  runId?: string;
}

type TaskRunKey = Pick<WsChatTaskStep, 'task' | 'runId'>;

/**
 * Whether a step belongs to a run: by `runId` when both carry one, by `task` otherwise.
 *
 * The fallback is not only for older servers: the rows the proxy re-states on its own (a
 * step marked failed / no_artifact, a retraction, a rewind — chatbot-api
 * ToolDrivenTaskStep#stepFrame) carry no run id, and they are always about the run of that
 * task the conversation is standing on.
 */
function sameTaskRun(run: TaskRunKey, step: TaskRunKey): boolean {
  return run.runId && step.runId ? run.runId === step.runId : run.task === step.task;
}

/** The run a walk belongs to: its task, and the first run id any of its steps carried. */
function runOf(walk: WsChatTaskStep[]): TaskRunKey {
  return { task: walk[0].task, runId: walk.find(s => s.runId)?.runId };
}

/** The NEWEST walk in `walks` that `step` belongs to — see {@link splitTaskRuns}. */
function lastWalkOf(walks: WsChatTaskStep[][], step: TaskRunKey): WsChatTaskStep[] | undefined {
  for (let i = walks.length - 1; i >= 0; i--) {
    if (sameTaskRun(runOf(walks[i]), step)) return walks[i];
  }
  return undefined;
}

/**
 * A list of steps split into one walk per run, each in arrival order, the walks in the order
 * their runs first appeared.
 *
 * One turn can carry steps of more than one run. Measured on test 2026-10-28 (conversation
 * 52a87d3a): the merchant picked a promotion direction, the turn walked promo-plan's
 * `propose` → `commit`, and the agent ALSO re-reported `publish` of the content-publish run
 * from twenty minutes earlier (a different runId). Drawn as one list, that put "Publishing
 * your content" inside the Promotion plan checklist — and split promo-plan's own `commit`
 * from its no_artifact re-statement, so "Locking in your plan" showed twice.
 *
 * A step joins the newest walk of its run; matched against the walk rather than against
 * each of its steps, so an id-less re-statement inside run A's walk cannot make a step of
 * run B (same task) look like A's.
 *
 * This is the one place "is this the same walk" is decided: the reducer uses it to pick the
 * persisted walk a between-turns step joins, and the checklist to draw one block per run.
 */
export function splitTaskRuns(steps: WsChatTaskStep[]): WsChatTaskStep[][] {
  const walks: WsChatTaskStep[][] = [];
  for (const s of steps) {
    const walk = lastWalkOf(walks, s);
    if (walk) walk.push(s);
    else walks.push([s]);
  }
  return walks;
}

/** The steps of `list` that belong to `step`'s run (its newest walk there), or undefined. */
function walkOfRun(
  list: WsChatTaskStep[] | undefined,
  step: TaskRunKey
): WsChatTaskStep[] | undefined {
  return list && list.length > 0 ? lastWalkOf(splitTaskRuns(list), step) : undefined;
}

/** The reasoning + tool activity of one completed turn, kept under its reply. */
export interface WsChatTurnTrace {
  thinking?: string;
  tools: WsChatToolEvent[];
  /** Task nodes walked during this turn, in arrival order. */
  taskSteps?: WsChatTaskStep[];
}

export interface WsChatRunState {
  phase: WsChatPhase;
  sessionId: string | null;
  messages: ChatMessage[];
  /** The in-flight assistant message being streamed (null when not streaming). */
  streaming: WsChatStreaming | null;
  /** Accumulated reasoning text for the current turn (from `thinking` tokens). */
  statusText: string | null;
  /** Tool calls / approvals seen in the current turn (cleared at turn end). */
  tools: WsChatToolEvent[];
  /** Task nodes entered in the current turn (cleared at turn end, like `tools`). */
  taskSteps: WsChatTaskStep[];
  /** Persisted per-turn traces, keyed by the assistant message id. */
  traces: Record<string, WsChatTurnTrace>;
  error: string | null;
  /** The error frame's own code (TURN_STALLED, …) or a client code, when it had one —
   *  `error` stays the raw text; what the merchant reads is built from both (turnErrorNotice). */
  errorCode: string | null;
  /** True once the credit pool is exhausted (backend CREDIT_EXHAUSTED frame). */
  creditExhausted: boolean;
  /** Monotonic counter for deterministic message ids (keeps the reducer pure). */
  seq: number;
}

/** Local (client-originated) actions, on top of the wire WsChatEvents. */
export type WsChatAction =
  | WsChatEvent
  | { type: 'user-message'; text: string }
  | {
      type: 'hydrate';
      messages: ChatMessage[];
      sessionId?: string | null;
      traces?: Record<string, WsChatTurnTrace>;
    }
  | {
      // Merge persisted history UNDER an in-flight turn (does NOT reset like
      // `hydrate`) — for opening a recent chat whose turn was resumed mid-flight.
      type: 'hydrate-merge';
      messages: ChatMessage[];
      sessionId?: string | null;
      traces?: Record<string, WsChatTurnTrace>;
    }
  | { type: 'approval-decided'; id: string; decision: WsChatApprovalDecision }
  | { type: 'interrupt' }
  | { type: 'reset' };

/**
 * The first `seq` no message in `messages` already uses.
 *
 * Message ids are minted from `seq` (`wsChat-N`, `wsChat-u-N`, `wsChat-published-N`, …), and a
 * replayed history does NOT use one number per message: a streamed narration that is folded
 * away (foldNarrationIntoTrace) or an echoed `done` consumed a number and left no message. So
 * `messages.length` lands BELOW ids that are already taken, and the next turn after a hydrate
 * was minted an id an earlier message already had — its trace overwrote that message's trace
 * (the old task walk was gone) and the brief card keyed by that id read as already answered,
 * so the new Quick setup card never showed (test f366e844, 2026-10-28).
 */
function seqAfter(messages: ChatMessage[]): number {
  let next = messages.length;
  for (const m of messages) {
    const n = /^wsChat-(?:[a-z]+-)?(\d+)$/.exec(m.id ?? '');
    if (n) next = Math.max(next, Number(n[1]) + 1);
  }
  return next;
}

/**
 * `state` with every id this page minted that `history` also uses minted again, past every number
 * either side has used.
 *
 * <p>A page that sends into an existing conversation before its history has arrived mints from 0:
 * it cannot know which numbers history used. History is replayed through this same reducer from 0
 * too, so the two collide — on a new page the merchant's first message was `wsChat-u-0`, which is
 * also the id of the conversation's FIRST user message, and the merge below, which skips history
 * messages it already has by id, dropped that one (dev 2026-10-28). The same goes for the live
 * reply's streaming id and the traces keyed by these ids. seqAfter (#1869) keeps the NEXT turn
 * clear of history's numbers; this does it for what was minted before history arrived.
 *
 * <p>Which side is re-minted: the page's own — every history message carries `fromHistory`, a
 * message minted here does not. History's ids stay what every reload replays them as, and nothing
 * outside this state holds the live ids. A history message merged in earlier (a conversation
 * re-opened while its turn still runs) carries `fromHistory` and keeps its id, so the id check
 * below still recognises it as already present.
 */
function clearOfHistoryIds(state: WsChatRunState, history: ChatMessage[]): WsChatRunState {
  const used = new Set(history.map(m => m.id));
  const clashes = (m: ChatMessage) => m.fromHistory !== true && used.has(m.id);
  const streamingClashes = !!state.streaming && used.has(state.streaming.id);
  if (!streamingClashes && !state.messages.some(clashes)) return state;

  let next = Math.max(state.seq, seqAfter(history), seqAfter(state.messages));
  const renamed = new Map<string, string>();
  const remint = (id: string) => {
    const family = /^(wsChat-(?:[a-z]+-)?)\d+$/.exec(id);
    const fresh = family ? `${family[1]}${next}` : `${id}-${next}`;
    next += 1;
    renamed.set(id, fresh);
    return fresh;
  };
  const messages = state.messages.map(m => (clashes(m) ? { ...m, id: remint(m.id) } : m));
  const streaming =
    state.streaming && streamingClashes
      ? { ...state.streaming, id: remint(state.streaming.id) }
      : state.streaming;
  const traces = Object.fromEntries(
    Object.entries(state.traces).map(([id, trace]) => [renamed.get(id) ?? id, trace])
  );
  return { ...state, messages, streaming, traces, seq: next };
}

export function initialWsChatState(messages: ChatMessage[] = []): WsChatRunState {
  return {
    phase: 'idle',
    sessionId: null,
    messages,
    streaming: null,
    statusText: null,
    tools: [],
    taskSteps: [],
    traces: {},
    error: null,
    errorCode: null,
    creditExhausted: false,
    seq: seqAfter(messages),
  };
}

/**
 * Text the model streamed BEFORE it went on to call a tool is narration
 * ("Now I need to fetch the keyword candidates…"), not the answer. zeroclaw
 * agrees: `done.full_response` carries only the final iteration's text, so the
 * finished bubble never contained it — but the live bubble did, and so did the
 * message the stuck-turn resync synthesises from a half-replayed turn
 * (wsChatApi.getConversationDetail), which is how a merchant on dev 2026-09-08
 * ended up with a persisted bubble reading "</think> Now I need to get the
 * channel keyword candidates…". Fold it into the trace's thinking text (kept
 * for the "is there anything here" test, never rendered) and clear the bubble.
 */
function foldNarrationIntoTrace(state: WsChatRunState): WsChatRunState {
  const text = state.streaming?.text ?? '';
  if (!state.streaming) return state;
  return {
    ...state,
    streaming: null,
    statusText: text.trim() ? (state.statusText ?? '') + text : state.statusText,
    phase: state.phase === 'streaming' ? 'thinking' : state.phase,
  };
}

/**
 * `a` followed by `b`, where the start of `b` may repeat the end of `a` — written once.
 *
 * A refresh mid-turn gets the turn from two places: history (what was recorded up to the moment
 * it was read) and the socket (frames from the moment it re-attached). chatbot-api records a
 * frame before it forwards it, so frames sent between the re-attach and the history read are in
 * BOTH: the end of the history half is the start of the live half.
 */
function joinOverlap<T>(a: T[], b: T[], same: (x: T, y: T) => boolean): T[] {
  for (let k = Math.min(a.length, b.length); k > 0; k--) {
    const tail = a.length - k;
    let match = true;
    for (let i = 0; i < k && match; i++) match = same(a[tail + i], b[i]);
    if (match) return [...a, ...b.slice(k)];
  }
  return [...a, ...b];
}

const sameStep = (x: WsChatTaskStep, y: WsChatTaskStep) =>
  x.task === y.task &&
  x.node === y.node &&
  x.status === y.status &&
  x.runId === y.runId &&
  x.failedReason === y.failedReason;

function joinTextOverlap(a: string, b: string): string {
  for (let k = Math.min(a.length, b.length); k > 0; k--) {
    if (a.endsWith(b.slice(0, k))) return a + b.slice(k);
  }
  return a + b;
}

/**
 * The live turn is the turn history ended in the middle of: fold that half back into it.
 *
 * <p>A refresh mid-turn rebuilds the conversation from history, which ends on a turn with no end
 * frame yet — getConversationDetail keeps it as one message (`turnInProgress`) carrying the steps
 * and tools recorded so far. The socket then re-attaches to that same turn (`resume`) and sends
 * only what comes NEXT — the proxy keeps no frame buffer. Left apart, the two halves are drawn
 * as two progress boxes of one run: test 2026-10-28 refreshed while Maya was researching and got
 * a static "Step 3 of 7" box (keywords ✓, research ✓) with a separate live "Step 4 of 7 · Writing
 * your content" box under it, merged into one only when the turn ended and history was re-read.
 * Before the refresh it had been one box. Folded back, the live turn continues the same walk, as
 * if the page had never reloaded.
 *
 * <p>Only the LAST message, and only that kind: a turn the merchant has just started sits after
 * it (their message is last), so a stale half-turn from an earlier, dead turn is never pulled
 * into a new one.
 */
function resumeHistoryTail(state: WsChatRunState): WsChatRunState {
  const last = state.messages[state.messages.length - 1];
  if (!last || last.role !== 'assistant' || !last.turnInProgress) return state;
  const { [last.id]: half, ...traces } = state.traces;
  const key = (t: WsChatToolEvent) => `${t.kind}:${t.id}`;
  const recorded = new Set((half?.tools ?? []).map(key));
  const text = last.content ?? '';
  // History's half ended on streamed text and nothing has streamed live yet: it is the start
  // of the bubble the live frames go on writing. Otherwise the two are joined like the steps.
  const restartStream = !!text && !state.streaming;
  return {
    ...state,
    messages: state.messages.slice(0, -1),
    traces,
    tools: [...(half?.tools ?? []), ...state.tools.filter(t => !recorded.has(key(t)))],
    taskSteps: joinOverlap(half?.taskSteps ?? [], state.taskSteps, sameStep),
    statusText: (half?.thinking ?? '') + (state.statusText ?? '') || null,
    // A fresh id, not the half's: it may be a `wsChat-incomplete-N` placeholder id, and the
    // message this turn completes into must not reuse an id history already handed out.
    streaming: restartStream
      ? { id: `wsChat-${state.seq}`, text }
      : state.streaming && text
        ? { ...state.streaming, text: joinTextOverlap(text, state.streaming.text) }
        : state.streaming,
    seq: restartStream ? state.seq + 1 : state.seq,
  };
}

export function wsChatReducer(state: WsChatRunState, action: WsChatAction): WsChatRunState {
  switch (action.type) {
    case 'interrupt':
      // End an in-flight turn (e.g. socket dropped mid-reply) without wiping the
      // conversation — drop the partial stream/trace and unstick the UI/input.
      return { ...state, phase: 'idle', streaming: null, statusText: null, tools: [], taskSteps: [] };

    case 'reset':
      return initialWsChatState();

    case 'hydrate':
      return {
        ...initialWsChatState(action.messages),
        sessionId: action.sessionId ?? null,
        traces: action.traces ?? {},
      };

    case 'hydrate-merge': {
      // Show persisted history WITHOUT wiping a live in-flight turn (YEP-709):
      // prepend the history messages that aren't already present, keep phase /
      // streaming / statusText / tools so the running turn keeps rendering. Dedup
      // by id AND role|content — the just-sent user message has a client id that
      // differs from its persisted id, so id alone would duplicate it.
      //
      // An id is only evidence of "already present" once this page's own ids are clear of
      // history's: before that, the merchant's first message here and history's first message
      // were both `wsChat-u-0`, and history's was dropped (clearOfHistoryIds).
      const live = clearOfHistoryIds(state, action.messages);
      const seenId = new Set(live.messages.map(m => m.id));
      const seenRC = new Set(live.messages.map(m => `${m.role}|${(m.content ?? '').trim()}`));
      const prepend = action.messages.filter(
        m => !seenId.has(m.id) && !seenRC.has(`${m.role}|${(m.content ?? '').trim()}`)
      );
      if (prepend.length === 0 && !action.sessionId) return live;
      const messages = [...prepend, ...live.messages];
      const merged = {
        ...live,
        sessionId: action.sessionId ?? live.sessionId,
        messages,
        // History-recorded traces first, then any live-turn traces win on overlap.
        traces: { ...(action.traces ?? {}), ...live.traces },
        seq: Math.max(live.seq, seqAfter(messages)),
      };
      // The socket re-attached (and started this live turn) before history arrived: if history
      // ends on that same turn, half-recorded, it is the start of this one — see resumeHistoryTail.
      return state.phase === 'thinking' || state.phase === 'streaming'
        ? resumeHistoryTail(merged)
        : merged;
    }

    case 'session':
      return { ...state, sessionId: action.sessionId ?? state.sessionId };

    case 'task-step': {
      const step: WsChatTaskStep = {
        task: action.task,
        taskName: action.taskName,
        node: action.node,
        label: action.label,
        index: action.index,
        total: action.total,
        status: action.status,
        failedReason: action.failedReason,
        runId: action.runId,
      };
      // A turn that has been taken back. The server refused the work this turn was
      // doing — Maya wrote a piece while the Quick setup card it had just opened was
      // still unanswered, so nothing was saved — and put the run back where the turn
      // found it. The rows drawn in between describe work done on guesses; showing
      // them, ticked OR warned, tells the merchant something went wrong in a flow
      // where nothing did. What is left is the one row they are actually on, which is
      // exactly what a well-behaved turn would have shown: the card, and the step
      // waiting for it.
      //
      // Only THAT run's rows go: a turn can also carry another run's steps, drawn as a
      // block of their own (TaskChecklist), and the rewind says nothing about them.
      if (action.rewound) {
        const keepOtherRuns = (list: WsChatTaskStep[]) => {
          const walk = walkOfRun(list, step);
          return [...list.filter(s => !walk?.includes(s)), step];
        };
        if (state.phase === 'idle') {
          for (let i = state.messages.length - 1; i >= 0; i--) {
            const id = state.messages[i].id;
            const trace = state.traces[id];
            const steps = trace?.taskSteps;
            if (!trace || !steps || !walkOfRun(steps, step)) continue;
            return {
              ...state,
              traces: { ...state.traces, [id]: { ...trace, taskSteps: keepOtherRuns(steps) } },
            };
          }
        }
        const folded = foldNarrationIntoTrace(state);
        return { ...folded, taskSteps: keepOtherRuns(folded.taskSteps) };
      }
      // Same replay guard as tool-call, for the same reason: a mid-turn reconnect
      // replays the turn's buffered frames. Scoped to the LAST entry rather than
      // the whole list on purpose — re-entering a node is a real and meaningful
      // event ("still waiting on you", or a revision that goes back), and
      // de-duplicating against the whole turn would silently swallow it. The last
      // entry OF THIS RUN: another run's step in between does not make an identical
      // repeat a new event.
      const sameAsLast = (list: WsChatTaskStep[] | undefined) => {
        const walk = walkOfRun(list, step);
        const last = walk?.[walk.length - 1];
        return (
          !!last && last.task === step.task && last.node === step.node && last.status === step.status
        );
      };
      // Between turns there is no live walk for this frame to join: the server
      // moved a run with no agent turn (a deterministic button action — e.g.
      // "Approve & Publish" reports the publish node itself), and the live list
      // is cleared by whatever starts next, before anything would render it. So
      // fold the step into the checklist the merchant is already looking at —
      // the newest persisted trace that walks this same RUN (by run id; by task
      // only when an id is missing — see splitTaskRuns). A different run of the same
      // task is not this walk. History replay funnels through this reducer too, so a
      // reload reconstructs the same view.
      if (state.phase === 'idle') {
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const id = state.messages[i].id;
          const trace = state.traces[id];
          const steps = trace?.taskSteps;
          if (!steps || !walkOfRun(steps, step)) continue;
          if (sameAsLast(steps)) return state;
          return {
            ...state,
            traces: { ...state.traces, [id]: { ...trace, taskSteps: [...steps, step] } },
          };
        }
        // No walk of this run on screen — keep it in the live list. Mostly
        // invisible, but the history tail-handling still picks it up on reload.
      }
      if (sameAsLast(state.taskSteps)) {
        return state;
      }
      // A step report is a tool call on the wire (REPORT_TASK_STEP), so text
      // streamed before it is narration too — see foldNarrationIntoTrace.
      const folded = foldNarrationIntoTrace(state);
      return { ...folded, taskSteps: [...folded.taskSteps, step] };
    }

    case 'thinking':
      // `thinking` frames stream reasoning token-by-token — accumulate them.
      return {
        ...state,
        phase: 'thinking',
        statusText: (state.statusText ?? '') + (action.text ?? ''),
        error: null,
        errorCode: null,
      };

    case 'tool-call': {
      // Replay idempotency: after a mid-turn reconnect the backend re-attaches
      // and replays the turn's buffered frames, so the same tool-call (same
      // stable id) can arrive again. Appending it a second time would render a
      // duplicate card for the same event — drop the replay instead.
      // (Scoped to kind==='call': an approval entry may share the id of the
      // call it approves — that call must still append normally.)
      if (action.id != null && state.tools.some(t => t.kind === 'call' && t.id === action.id)) {
        return state;
      }
      // `ASK_USER_QUESTION` parks the turn waiting for the user's answer. Drop
      // out of the busy state so the question card's answer (and the input box)
      // aren't blocked by the `isBusy` guard while we wait. Other tools keep the
      // turn "thinking".
      const isAsk = canonicalWsToolName(action.name) === ASK_QUESTION_TOOL_NAME;
      const folded = foldNarrationIntoTrace(state);
      return {
        ...folded,
        phase: isAsk ? 'idle' : folded.phase === 'idle' ? 'thinking' : folded.phase,
        tools: [
          ...folded.tools,
          {
            id: action.id ?? `t-${state.tools.length}`,
            name: action.name,
            kind: 'call',
            args: action.args,
            status: isAsk ? 'done' : 'running',
          },
        ],
      };
    }

    case 'tool-result': {
      // Replay idempotency (mid-turn reconnect replays buffered frames): a
      // result whose id already belongs to a COMPLETED call is the same event
      // arriving again — without this guard it fails the `status !== 'done'`
      // match below and gets APPENDED as a second finished call, rendering a
      // duplicate report card for one tool run. (Report persistence already
      // dedupes by eventId, so the saved report stays single — only the chat
      // showed doubles.)
      if (
        action.id != null &&
        state.tools.some(t => t.kind === 'call' && t.id === action.id && t.status === 'done')
      ) {
        return state;
      }
      // Complete the matching running call; if none matched, add a finished one.
      let matched = false;
      const tools = state.tools.map(t => {
        if (
          !matched &&
          t.kind === 'call' &&
          t.status !== 'done' &&
          (t.id === action.id || t.name === action.name)
        ) {
          matched = true;
          return { ...t, status: 'done' as const, output: action.output, eventId: action.eventId ?? t.eventId ?? null };
        }
        return t;
      });
      if (!matched) {
        tools.push({
          id: action.id ?? `t-${state.tools.length}`,
          name: action.name,
          kind: 'call',
          status: 'done',
          output: action.output,
          eventId: action.eventId ?? null,
        });
      }
      return { ...state, tools };
    }

    // A failed publish (or explicit request) needs the user to connect a
    // channel. Represent it as a finished synthetic tool call so it flows
    // through the same connect-card render path as `initiate_composio_connection`.
    case 'channel-connect':
      return {
        ...state,
        phase: state.phase === 'idle' ? 'thinking' : state.phase,
        tools: [
          ...state.tools,
          {
            id: `channel-connect-${state.tools.length}`,
            name: 'initiate_composio_connection',
            kind: 'call',
            status: 'done',
            args: { toolkit: action.toolkit },
            output: action.authUrl,
          },
        ],
      };

    // Deterministic publish success: append a finished assistant turn whose
    // trace carries a RECORD_PUBLISHED_CONTENT tool, so the merchant sees the
    // exact same "Published" confirmation card as the agent (typed) path.
    case 'record-published': {
      const id = `wsChat-published-${state.seq}`;
      const msg: ChatMessage = {
        id,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
      };
      const tool: WsChatToolEvent = {
        id: `${id}-record`,
        name: 'RECORD_PUBLISHED_CONTENT',
        kind: 'call',
        status: 'done',
        args: {
          channel: action.channel,
          title: action.title,
          external_url: action.externalUrl,
          status: 'PUBLISHED',
        },
      };
      return {
        ...state,
        messages: [...state.messages, msg],
        traces: { ...state.traces, [id]: { tools: [tool] } },
        // Clear the live "publishing…" thinking bar this turn showed.
        streaming: null,
        statusText: null,
        tools: [],
        taskSteps: [],
        phase: 'idle',
        seq: state.seq + 1,
      };
    }

    case 'tool-approval':
      return {
        ...state,
        phase: state.phase === 'idle' ? 'thinking' : state.phase,
        tools: [
          ...state.tools,
          {
            id: action.id ?? `t-${state.tools.length}`,
            name: action.name,
            kind: 'approval',
            summary: action.summary,
            timeoutSec: action.timeoutSec,
          },
        ],
      };

    case 'approval-decided':
      return {
        ...state,
        tools: state.tools.map(t => (t.id === action.id ? { ...t, decided: action.decision } : t)),
      };

    case 'activity':
      return {
        ...state,
        phase: state.phase === 'idle' ? 'thinking' : state.phase,
        statusText: action.detail || action.name,
      };

    case 'user-message': {
      const msg: ChatMessage = {
        id: `wsChat-u-${state.seq}`,
        role: 'user',
        content: action.text,
        timestamp: Date.now(),
      };
      // New turn — clear the previous turn's reasoning/tools, show "thinking".
      return {
        ...state,
        messages: [...state.messages, msg],
        phase: 'thinking',
        statusText: null,
        tools: [],
        taskSteps: [],
        error: null,
        errorCode: null,
        seq: state.seq + 1,
      };
    }

    case 'text-delta': {
      // Keep the reasoning/tool trace visible while the answer streams.
      if (!state.streaming) {
        return {
          ...state,
          phase: 'streaming',
          streaming: { id: `wsChat-${state.seq}`, text: action.delta },
          seq: state.seq + 1,
        };
      }
      return {
        ...state,
        phase: 'streaming',
        streaming: { ...state.streaming, text: state.streaming.text + action.delta },
      };
    }

    case 'resume': {
      // Re-attached to a still-streaming turn after a refresh/detach — or (chatbot-api #2412)
      // a message the merchant sent mid-turn is now going up as a turn of its own. Show the
      // working state again; live frames follow. From `error` too: a queued message is still
      // sent after the turn in front of it failed. The error text stays until the new turn's
      // first frame replaces it. pendingReply is managed by the manager, not here.
      if (!action.replyInFlight) return state;
      // After a refresh the history already loaded ends in the middle of this very turn: carry
      // on from there instead of starting a second progress box (resumeHistoryTail).
      const resumed = resumeHistoryTail(state);
      if (resumed !== state) {
        return { ...resumed, phase: resumed.streaming ? 'streaming' : 'thinking' };
      }
      return state.phase === 'idle' || state.phase === 'error'
        ? { ...state, phase: 'thinking' }
        : state;
    }

    case 'text-reset':
      return state.streaming ? { ...state, streaming: { ...state.streaming, text: '' } } : state;

    case 'message-complete': {
      const text = action.text || state.streaming?.text || '';
      // Task steps count as a trace on their own: a turn can walk several nodes
      // while calling no tool this side of the wire (the work happens inside one
      // MCP call, or the node is just waiting on the merchant). Leaving them out
      // of this test would drop the whole progress trail on exactly those turns.
      const hasTrace =
        !!state.statusText?.trim() || state.tools.length > 0 || state.taskSteps.length > 0;
      if (!text && !hasTrace) {
        return { ...state, phase: 'idle', streaming: null, statusText: null, tools: [], taskSteps: [] };
      }
      // [3.5 新-1] 防重:同一句旁白连着来两遍时,只留一条。
      //
      // 定位结论(见 manager.route):`done` 帧 → message-complete → 这里**无条件**追加,
      // 是全流程里唯一没有幂等的追加路径(hydrate 是整体替换、hydrate-merge 按
      // role|content 去重、草稿/工具卡各有自己的去重)。批量生成时 Maya 那句
      // 「Committed — N posts on your calendar…」由服务端一模一样地发了两个 `done`,
      // 于是屏上并排两条。真正的源头在 Agent/代理(不该发两次),但前端这条追加路径
      // 也该有和其它路径一样的防线 —— 一句一字不差的旁白重复出现,从来不是第二个答案。
      //
      // 收得很窄,不误伤:① 只在这一回合**没有自己的 trace**(纯旁白、无工具无思考无
      // 任务步)时才折叠 —— 带卡/带工具的回合内容各不相同,压根撞不上;② 只和**紧邻的
      // 上一条**assistant 比,中间隔了用户消息或别的卡就不算重复(批量的多条草稿回合
      // 内容不同,不受影响)。
      const prev = state.messages[state.messages.length - 1];
      const isEchoOfPrev =
        !hasTrace &&
        !!text.trim() &&
        prev?.role === 'assistant' &&
        (prev.content ?? '').trim() === text.trim();
      if (isEchoOfPrev) {
        return {
          ...state,
          streaming: null,
          statusText: null,
          tools: [],
          taskSteps: [],
          phase: 'idle',
        };
      }
      const id = state.streaming?.id ?? `wsChat-${state.seq}`;
      const msg: ChatMessage = {
        id,
        role: 'assistant',
        content: text,
        timestamp: Date.now(),
      };
      // Persist this turn's reasoning + tools + progress under the reply (Zed-style).
      const traces = hasTrace
        ? {
            ...state.traces,
            [id]: {
              thinking: state.statusText ?? undefined,
              tools: state.tools,
              // Omitted rather than stored empty: most turns are not tasks, and an
              // empty array here would make every archived trace look like one.
              taskSteps: state.taskSteps.length > 0 ? state.taskSteps : undefined,
            },
          }
        : state.traces;
      return {
        ...state,
        messages: [...state.messages, msg],
        traces,
        streaming: null,
        statusText: null,
        tools: [],
        taskSteps: [],
        phase: 'idle',
        seq: state.streaming ? state.seq : state.seq + 1,
      };
    }

    case 'error':
      return {
        ...state,
        phase: 'error',
        streaming: null,
        statusText: null,
        tools: [],
        taskSteps: [],
        error: action.message,
        errorCode: action.code ?? null,
      };

    case 'credit-exhausted':
      return {
        ...state,
        phase: 'error',
        streaming: null,
        statusText: null,
        tools: [],
        taskSteps: [],
        creditExhausted: true,
        error: action.message ?? 'You are out of credits.',
      };

    default:
      return state;
  }
}
